import { useState, useEffect, useMemo } from 'react';
import { HappyWebSystemError } from '../components/base/SystemComponent.js';

const DEFAULT_CONFIG = {
    threshold: [
        0,
        0.1,
        0.2,
        0.3,
        0.4,
        0.5,
        0.6,
        0.7,
        0.8,
        0.9,
        1
    ],
    triggerOnce: false,
    ratio: 1,
    containerMargin: "0px"
};
/**
 * Wrapper hook for using intersection observer.
 * Allows detection of elements intersection and visibility within browser viewport.
 */ const useIntersectionObserver = (ref, config = {})=>{
    const [intersecting, setIntersecting] = useState(false);
    useEffect(()=>{
        if (!ref) {
            throw new HappyWebSystemError("useIntersectionObserver", "Must provide a ref for intersection.");
        }
        // Protect against running in a test environment. Included here for brevity as the jsdom test environment doesn't
        // support it, which means every test related to this dependency would have to manually mock it.
        if (!window.IntersectionObserver) {
            return;
        }
        // Target element for checking intersection
        const el = ref.current;
        // ID of current request animation frame. Allows cancelling an 'in-flight' animation frame request in the case
        // where the observer callback fire's multiple times before the animation frame callback is run.
        let reqFrameID = undefined;
        const options = {
            ...DEFAULT_CONFIG,
            ...config
        };
        const observer = new window.IntersectionObserver((entries)=>{
            if (options.triggerOnce) {
                if (entries[0].isIntersecting) {
                    setIntersecting(true);
                    if (!!el) {
                        observer.unobserve(el);
                    }
                }
            } else {
                reqFrameID = window.requestAnimationFrame(()=>setIntersecting(entries[0].isIntersecting && entries[0].intersectionRatio >= options.ratio));
            }
        }, {
            ...options,
            rootMargin: options.containerMargin,
            root: options?.containerRef?.current
        });
        if (!!el) {
            observer.observe(el);
        }
        return ()=>{
            // Cleanup the current animation frame request and observer. Important to prevent unintended memory usage.
            if (!!el) {
                observer.unobserve(el);
            }
            if (!!reqFrameID) {
                window.cancelAnimationFrame(reqFrameID);
            }
        };
    }, [
        ref,
        config,
        setIntersecting
    ]);
    const value = useMemo(()=>({
            intersecting
        }), [
        intersecting
    ]);
    return value;
};

export { useIntersectionObserver };
